import { useState } from 'react'
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { nextPage } from 'components/RCPro/helpers/nav'
import reactStringReplace from 'react-string-replace'
import Edit from 'components/assets/icons/Edit'
import Eye from 'components/assets/icons/Eye'
import Select from 'components/common/Select/Select'
import Checkbox from 'components/common/Checkbox/Checkbox'
import Arrow from 'components/assets/icons/Arrow'
import FirstName from 'components/RCPro/components/form/FirstName/FirstName'
import LastName from 'components/RCPro/components/form/LastName/LastName'
import SelectCompany from 'components/RCPro/components/form/SelectCompany/SelectCompany'
import Activity from 'components/RCPro/components/form/Activity/Activity'
import Email from 'components/RCPro/components/form/Email/Email'
import PhoneNumber from 'components/RCPro/components/form/PhoneNumber/PhoneNumber'
import Button from 'components/common/Button/Button'
import { formParams } from 'constants/inputValidation'
import style from './Resume.module.scss'
import SideForm from 'components/RCPro/components/SideForm/SideForm'
import Link from 'components/common/Link/Link'

const RCProResume = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state, pathname } = useLocation()
  const [ nextPageTransition ] = useOutletContext()
  const [ acceptTerms, setAcceptTerms ] = useState(false)
  const [ edit, setEdit ] = useState(false)
  const {
    resume,
    estimate: { productName },
    activity: { domains },
    layout: { next }
  } = state.texts

  const domainsNames = domains.map( domain => ({
    label: domain.sector,
    value: domain.sector
  }))

  // Initialize form
  const {
    register,
    formState: { isValid, errors },
    trigger,
    handleSubmit,
    setValue,
    control,
    watch
  } = useForm({
    ...formParams,
    values: {
      firstname: state?.firstname,
      lastname: state?.lastname,
      company: state?.company,
      domain: state?.domain,
      activity: state?.activity,
      email: state?.email,
      phone: state?.phone
    },
  })

  const dateToString = (date) => {
    const [ year, month, day ] = date.toISOString().split('T')[0].split('-')
    return `${day}/${month}/${year}`
  }

  const toggleEdit = () => {
    setEdit(!edit)
  }

  const onCompanyChange = datas => {
    setValue('company', datas)
    trigger()
  }

  const onDomainChange = datas => {
    const { sector, sectorCode } = domains.find( domain => domain.sector === datas )
    setValue('domain', { sector, sectorCode })
    setValue('activity', '')
    trigger()
  }

  const onActivityChange = datas => {
    setValue('activity', datas)
    trigger()
  }

  const onSubmit = handleSubmit(async data => {
    navigate(pathname, { state: { ...state, ...data } })
    toggleEdit()
  })

  const submit = () => {
    nextPage({ pathname, state, navigate })
    nextPageTransition()
  }

  return (
    <>
      <h1>{resume.title}</h1>

      <div className={style.bloc}>
        <Edit className={style.edit} onClick={() => setEdit(true)} />
        <h2>{resume.insured}</h2>
        {state.firstname} {state.lastname}
        <br/>{state.activity} à Paris 14 (75014)
        <br/>{resume.turnover} {state.turnover}
        <br/>{resume.start} {dateToString(state.activation)}
        <br/>{resume.end} 23/05/2026
      </div>

      <div className={style.bloc}>
        <h2>{resume.documents}</h2>
        <ul className={style.documents}>
          <li>
            {productName}
            <Link
              label={<><Eye />{resume.read}</>}
              to={''}
            />
          </li>
        </ul>
      </div>

      <div className={style.bloc}>
        <h2>{resume.levy}</h2>
        180,00€/an
      </div>

      <Checkbox
        id="agree"
        className={style.checkbox}
        label={
          reactStringReplace(resume.legalterms, '{{link}}', (match, i) => (
            <Link
              key={`link-${i}`}
              label={resume.link}
              to={''}
            />
          ))
        }
        onChange={() => setAcceptTerms(!acceptTerms)}
      />

      <Button
        label={<>{next} <Arrow /></>}
        onClick={submit}
        disabled={!acceptTerms}
        type="primary"
      />

      <SideForm
        title={resume.insured+' ✍️'}
        onSubmit={onSubmit}
        toggleEdit={toggleEdit}
        active={edit}
        content={
          <>
            <FirstName
              register={register}
              error={errors.firstname}
            />

            <LastName
              register={register}
              error={errors.lastname}
            />

            <SelectCompany
              register={register}
              error={errors.company}
              onChange={onCompanyChange}
            />

            <Select
              options={domainsNames}
              onChange={val => onDomainChange(val.value)}
              label={t('mainReporting:form.domain.label')}
              placeholder={t('mainReporting:form.domain.placeholder')}
              value={watch('domain')}
              styles={{
                control:{
                  background: '#FFF'
                }
              }}
            />

            <Activity
              register={register}
              error={errors.activity}
              onChange={onActivityChange}
              activities={domains.find( domain => domain.sector === watch('domain').sector ).activities}
            />

            <Email
              register={register}
              error={errors.email}
            />

            <PhoneNumber
              control={control}
              error={errors.phone}
              value={state?.phone?.number || ''}
            />

            <Button
              label={resume.editButton}
              onClick={handleSubmit}
              disabled={!isValid}
              type="primary"
            />
          </>
        }
      />
    </>
  )
}

export default RCProResume